import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useNavigate } from "#src/Routers/hooks";
import { AnnotationDetailRoutePath } from "#src/routes/ctr-annotation";
import { useQuery } from "@tanstack/react-query";
import { Banner, Button, Icon, Skeleton } from "@validereinc/common-components";
import {
  Resources,
  WorkflowAdapter,
  WorkflowTaskStatus,
  WorkflowTaskType,
  WorkflowType,
} from "@validereinc/domain";
import { FeatureFlags } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import React from "react";
import styles from "../WorkflowFormSubmission.module.scss";

const cx = classNames.bind(styles);

export const isAnnotationSubmission = (
  workflow?: WorkflowType,
  formSchemaId?: string
) =>
  workflow?.variables?.$IS_CTR_ANNOTATION === "true" &&
  workflow?.variables?.$CTR_ANNOTATION_FORM_SCHEMA_ID === formSchemaId;

const WorkflowTaskFormSubmissionStartCTRButton = ({
  workflowTask,
  encodedAndSerializedDefaultValues,
  formSchemaId,
  workflow,
}: {
  workflowTask?: WorkflowTaskType;
  encodedAndSerializedDefaultValues: string;
  formSchemaId?: string;
  workflow?: WorkflowType;
}) => {
  const [isCTRFeatureAvailable] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.CTRAnnotation,
  });

  const navigate = useNavigate();

  const {
    v2: {
      isLoading: isLoadingActiveUser,
      userInfo: { user: activeUser },
    },
  } = useAuthenticatedContext();

  const isAnnotation = isAnnotationSubmission(workflow, formSchemaId);

  const canTaskBeCompleted =
    workflowTask?.status === WorkflowTaskStatus.OPEN ||
    workflowTask?.status === WorkflowTaskStatus.OVERDUE;

  const requestPayload: Parameters<typeof WorkflowAdapter.forms.getList>[0] = {
    workflowId: workflowTask?.workflow_id ?? "",
    page: 1,
    pageSize: 500,
  };

  const userIdsAlreadySubmittedCTRFormQuery = useQuery({
    queryKey: [Resources.WORKFLOW, "ctr_form_created_by_ids", requestPayload],
    queryFn: async () => {
      const { data } = await WorkflowAdapter.forms.getList(requestPayload);
      if (workflow?.variables?.$CTR_ANNOTATION_FORM_SCHEMA_ID) {
        const ctrAnnotationForms = data.filter(
          (form) =>
            form.form_schema_id ===
            workflow?.variables?.$CTR_ANNOTATION_FORM_SCHEMA_ID
        );

        const creatorIds = ctrAnnotationForms.map((form) => form.created_by);

        return creatorIds;
      }
      return [];
    },
    enabled:
      Boolean(workflowTask?.workflow_id) &&
      isAnnotation &&
      Boolean(workflow?.variables?.$CTR_ANNOTATION_FORM_SCHEMA_ID) &&
      canTaskBeCompleted,
  });

  const workflowId = workflow?.id;

  if (!workflowTask?.id || !workflowId || !isAnnotation || !canTaskBeCompleted)
    return null;

  if (!isCTRFeatureAvailable)
    return (
      <Banner
        titleText="Please contact the support team to enable the annotation feature for your organization."
        variant="warning"
      />
    );

  const canUserSubmitCTRForm =
    activeUser?.id &&
    !userIdsAlreadySubmittedCTRFormQuery.data?.includes(activeUser.id);

  return (
    <Skeleton
      isLoading={
        userIdsAlreadySubmittedCTRFormQuery.isFetching || isLoadingActiveUser
      }
    >
      <div
        className={cx("contentsContainer")}
        style={{ padding: "16px", display: "flex", justifyContent: "center" }}
      >
        {canUserSubmitCTRForm ? (
          <Button
            variant="primary"
            size="x-small"
            slotLeft={
              <Icon
                variant="pencil-simple"
                size={24}
              />
            }
            onClick={() => {
              navigate(
                AnnotationDetailRoutePath.toLinkParts({
                  queryParams: {
                    formSchemaId,
                    taskId: workflowTask.id,
                    workflowId,
                    "default-values": encodedAndSerializedDefaultValues,
                  },
                })
              );
            }}
          >
            Start Annotation
          </Button>
        ) : (
          <Banner
            titleText="You have already submitted an annotation for this set of tubes."
            variant="info"
          />
        )}
      </div>
    </Skeleton>
  );
};

export default WorkflowTaskFormSubmissionStartCTRButton;
